<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type {
  FetchArticleResponse,
  NestedArticleResponse,
  FetchTicketResponse,
  AddArticlesToTicketType,
} from 'src/api/models';
import { useNotification } from 'src/composables/useNotification';
import { useConfirm } from 'src/composables/useConfirm';
import { useTicketService } from 'src/services/ticket-service';
import { getFetchArticle, getFetchArticleOption } from 'src/api/queries/order-portal-bff';
import { formatCurrency } from 'src/utils/currency';
import ArticlesTable from '../ticket/article/ArticlesTable.vue';
import TicketEditArticleForm from './TicketEditArticleForm.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

interface ExtendedArticle extends FetchArticleResponse {
  quantity?: number;
  association_id?: string;
  type?: AddArticlesToTicketType;
  price?: number | null;
}

interface ExtendedNestedArticle extends Partial<NestedArticleResponse> {
  quantity?: number;
  association_id?: string;
  type?: AddArticlesToTicketType;
  price?: number | null;
}

const props = defineProps<{ ticket: FetchTicketResponse }>();
const emit = defineEmits<{ 'refresh-ticket': [] }>();

const articles = defineModel<ExtendedNestedArticle[]>('articles', { required: true });
const detailedArticles = ref<ExtendedArticle[]>([]);
const { showSuccess, showError } = useNotification();
const { confirm } = useConfirm();
const { updateArticleQuantity, updateArticlePrice, removeArticleFromTicket } = useTicketService();

const loading = ref(false);
const refreshing = ref(false);
const articlesTableRef = ref<InstanceType<typeof ArticlesTable> | null>(null);

const totalArticleValue = computed(() =>
  detailedArticles.value.reduce((sum, article) => {
    const quantity = article.quantity || 1;
    const price = article.price ?? article.sale_price ?? 0;
    return sum + price * quantity;
  }, 0),
);

const formattedTotal = computed(() => formatCurrency(totalArticleValue.value));

function handleArticleAdded() {
  emit('refresh-ticket');
  detailedArticles.value = [];
}

async function handleQuantityUpdate(associationId: string, quantity: number) {
  articlesTableRef.value?.setUpdating(associationId, true);
  try {
    await updateArticleQuantity(associationId, quantity);
    const article = detailedArticles.value.find((a) => a.association_id === associationId);
    if (article) article.quantity = quantity;
  } catch {
    emit('refresh-ticket');
  } finally {
    articlesTableRef.value?.setUpdating(associationId, false);
  }
}

async function handlePriceUpdate(associationId: string, price: number) {
  articlesTableRef.value?.setUpdating(associationId, true);
  try {
    await updateArticlePrice(associationId, price);
    const article = detailedArticles.value.find((a) => a.association_id === associationId);
    if (article) article.price = price;
  } catch {
    emit('refresh-ticket');
  } finally {
    articlesTableRef.value?.setUpdating(associationId, false);
  }
}

async function handleRemoveArticle(article: ExtendedArticle, event: Event) {
  if (!article.association_id || !props.ticket.id) return;
  const { onAccept } = confirm(event);
  onAccept(async () => {
    if (props.ticket.id && article.association_id) {
      await removeArticleFromTicket(props.ticket.id, article.association_id);
      showSuccess({ message: 'Article removed successfully' });
      emit('refresh-ticket');
      detailedArticles.value = [];
    }
  });
}

async function handleFetchMissingArticleDetails() {
  loading.value = true;
  try {
    const ids = articles.value
      .map((a) => a.id)
      .filter((id) => id && !detailedArticles.value.find((a) => a.id === id)) as string[];

    if (ids.length > 0) {
      const response = await Promise.all(ids.map((id) => getFetchArticleOption(id)));

      detailedArticles.value = [
        ...detailedArticles.value,
        ...response.map((article) => {
          const original = articles.value.find((a) => a.id === article.id);
          return {
            ...article,
            quantity: original?.quantity ?? 1,
            association_id: original?.association_id,
            type: original?.type,
            price: original?.price,
          } as ExtendedArticle;
        }),
      ];
    }
  } catch {
    showError({ message: 'Failed to fetch article details' });
  } finally {
    loading.value = false;
  }
}

function handleRefresh() {
  refreshing.value = true;
  detailedArticles.value = [];
  emit('refresh-ticket');
  refreshing.value = false;
  showSuccess({ message: 'Refreshing ticket data...' });
}

watch(articles, () => handleFetchMissingArticleDetails(), { immediate: true });
</script>

<template>
  <div class="flex min-h-0">
    <div class="flex-1 p-8 min-w-0">
      <TicketEditSectionHeader title="Articles" :count="detailedArticles.length" class="mb-4">
        <template #badges>
          <span v-if="totalArticleValue" class="total-badge">
            {{ formattedTotal }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center gap-1.5">
            <button class="action-btn" :class="{ 'opacity-50': refreshing }" @click="handleRefresh">
              <i class="pi pi-refresh" :class="{ 'pi-spin': refreshing }" style="font-size: 13px" />
            </button>
          </div>
        </template>
      </TicketEditSectionHeader>

      <ArticlesTable
        ref="articlesTableRef"
        :articles="detailedArticles"
        :loading="loading"
        :total-article-value="totalArticleValue"
        @update-quantity="handleQuantityUpdate"
        @update-price="handlePriceUpdate"
        @remove-article="handleRemoveArticle"
      />
    </div>

    <TicketEditArticleForm
      :ticket-id="ticket.id!"
      @article-added="handleArticleAdded"
    />
  </div>
</template>

<style scoped>
/* total-badge is article-specific (green price pill) */
.total-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: var(--p-green-50);
  color: var(--p-green-700);
}
:root[class*='app-dark'] .total-badge {
  background: color-mix(in srgb, var(--p-green-500) 15%, transparent);
  color: var(--p-green-400);
}
</style>
